import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useRecordings, Recording } from "@/hooks/useRecordings";
import { Video, Download, Trash2, Play, RefreshCw, HardDrive } from "lucide-react";
import { format } from "date-fns";

interface RecordingManagerProps {
  meetingId?: string;
  showMeetingTitle?: boolean;
}

export default function RecordingManager({ meetingId, showMeetingTitle = true }: RecordingManagerProps) {
  const { recordings, loading, storageUsed, formatFileSize, deleteRecording, downloadRecording, refreshRecordings } = useRecordings(meetingId);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState<Recording | null>(null);
  const [playingRecording, setPlayingRecording] = useState<Recording | null>(null);

  const handleDeleteClick = (recording: Recording) => {
    setSelectedRecording(recording);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedRecording) {
      const filePath = `${selectedRecording.meeting_id}/${selectedRecording.name}`;
      await deleteRecording(selectedRecording.id, filePath);
      setDeleteDialogOpen(false);
      setSelectedRecording(null);
    }
  };

  const handlePlayClick = (recording: Recording) => {
    setPlayingRecording(recording);
  };

  const handleDownloadClick = async (recording: Recording) => {
    const filePath = `${recording.meeting_id}/${recording.name}`;
    await downloadRecording(filePath, recording.name);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Video className="h-8 w-8 animate-pulse text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Storage Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Recordings</CardTitle>
            <Video className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{recordings.length}</div>
            <p className="text-xs text-muted-foreground">
              Available recordings
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Storage Used</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatFileSize(storageUsed)}</div>
            <p className="text-xs text-muted-foreground">
              Total storage usage
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Actions</CardTitle>
            <RefreshCw className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Button variant="outline" size="sm" onClick={refreshRecordings} className="w-full">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh List
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Video Player */}
      {playingRecording && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Now Playing: {playingRecording.name}</span>
              <Button variant="ghost" size="sm" onClick={() => setPlayingRecording(null)}>
                Close
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <video
              controls
              className="w-full max-h-96 bg-black rounded-lg"
              src={playingRecording.url}
            >
              Your browser does not support the video tag.
            </video>
          </CardContent>
        </Card>
      )}

      {/* Recordings List */}
      <Card>
        <CardHeader>
          <CardTitle>Recordings</CardTitle>
          <CardDescription>
            {meetingId ? 'Recordings for this meeting' : 'All meeting recordings'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recordings.length === 0 ? (
            <div className="text-center py-8">
              <Video className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No recordings available</p>
              <p className="text-sm text-muted-foreground mt-2">
                Recordings will appear here after meetings are recorded
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {recordings.map((recording) => (
                <div
                  key={recording.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors gap-3"
                >
                  <div className="flex-1 space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Video className="h-4 w-4 text-primary flex-shrink-0" />
                      <h4 className="font-medium break-words">{recording.name}</h4>
                      <Badge variant="secondary" className="flex-shrink-0">{formatFileSize(recording.size)}</Badge>
                    </div>
                    {showMeetingTitle && recording.meeting_title && (
                      <p className="text-sm text-muted-foreground truncate">
                        Meeting: {recording.meeting_title}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Recorded: {format(new Date(recording.created_at), 'PPpp')}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0 justify-end sm:justify-start">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePlayClick(recording)}
                      title="Play recording"
                    >
                      <Play className="h-4 w-4" />
                      <span className="sr-only">Play</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadClick(recording)}
                      title="Download recording"
                    >
                      <Download className="h-4 w-4" />
                      <span className="sr-only">Download</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(recording)}
                      title="Delete recording"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                      <span className="sr-only">Delete</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recording?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedRecording?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
